import { useSettings } from "@ext/enterprise/components/admin/contexts/SettingsContext";
import { useEditorRevoke } from "@ext/enterprise/components/admin/settings/members/hooks/useEditorRevoke";
import { useEditorSlotStatus } from "@ext/enterprise/components/admin/settings/members/hooks/useEditorSlotStatus";
import { useMemberAccessTable } from "@ext/enterprise/components/admin/settings/members/hooks/useMemberAccessTable";
import { getAccessRowId, type MemberAggregate } from "@ext/enterprise/components/admin/settings/members/model/Member";
import { limitRepositoryEditorRoles } from "@ext/enterprise/components/admin/settings/members/model/roleRules";
import { useCallback, useEffect, useMemo, useState } from "react";

export interface MemberRoles {
	isEditor: boolean;
	isAdmin: boolean;
	aiAgentAccess: boolean;
}

interface UseMemberAccessFormArgs {
	email: string;
	aggregate: MemberAggregate;
	initialRoles: MemberRoles;
	isOwner: boolean;
}

export const useMemberAccessForm = ({ email, aggregate, initialRoles, isOwner }: UseMemberAccessFormArgs) => {
	const [isAdmin, setIsAdmin] = useState(initialRoles.isAdmin);
	const [isEditor, setIsEditor] = useState(initialRoles.isEditor);
	const [aiAgentAccess, setAiAgentAccess] = useState(initialRoles.aiAgentAccess);

	const { getUserRules } = useSettings();

	useEffect(() => {
		setIsAdmin(initialRoles.isAdmin);
		setIsEditor(initialRoles.isEditor);
		setAiAgentAccess(initialRoles.aiAgentAccess);
	}, [initialRoles]);

	const handleSetIsAdmin = useCallback(
		(val: boolean) => {
			if (!isOwner) setIsAdmin(val);
		},
		[isOwner],
	);

	const handleSetIsEditor = useCallback((val: boolean) => {
		setIsEditor(val);
		if (!val) setIsAdmin(false);
	}, []);

	const { editorSlotsFull, effectiveEditorsUsed } = useEditorSlotStatus({ email, isEditor, aggregate });

	const roleRules = useMemo(() => limitRepositoryEditorRoles(getUserRules(), isEditor), [getUserRules, isEditor]);

	const {
		originalAccesses,
		accessRows,
		accessColumns,
		accessRowVersions,
		validateAccesses,
		accessPreselected,
		accessSelection,
		setAccessSelection,
		selectedAccesses,
		removeSelectedAccesses,
		accessPickerState,
		addAccesses,
		demoteAccessesToReader,
	} = useMemberAccessTable({ email, aggregate, roleRules });

	const editorRevoke = useEditorRevoke({
		accesses: accessRows,
		setIsEditor: handleSetIsEditor,
		demoteAccessesToReader,
	});

	return {
		data: {
			email,
			isAdmin,
			setIsAdmin: handleSetIsAdmin,
			isEditor,
			setIsEditor: editorRevoke.setIsEditor,
			aiAgentAccess,
			setAiAgentAccess,
			isOwner,
		},
		access: {
			original: originalAccesses,
			rows: accessRows,
			rowVersions: accessRowVersions,
			getId: getAccessRowId,
			columns: accessColumns,
			validate: validateAccesses,
			preselected: accessPreselected,
			selection: accessSelection,
			setSelection: setAccessSelection,
			selected: selectedAccesses,
			removeSelected: removeSelectedAccesses,
			picker: {
				...accessPickerState,
				picked: addAccesses,
			},
		},
		roleRules,
		editorRevokeDialog: editorRevoke.dialog,
		editorSlotsFull,
		editorCount: aggregate.editorsCount,
		effectiveEditorsUsed,
	};
};

export type MemberAccessFormState = ReturnType<typeof useMemberAccessForm>;
